import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Tabs, Spinner, SimpleButton, LoadingButton, Select, Toast, useStateContext } from '@veda/dashboard-ui';
import { useProcedimento, useScadenze } from '../hooks/useProcedimento';
import { useAuthUser } from '../hooks/useAuthUser';
import { usePppAiContext } from '../hooks/usePppAiContext';
import TimelineFasi from '../components/TimelineFasi';
import ScadenzeWidget from '../components/ScadenzeWidget';
import AttiTab from '../components/AttiTab';
import DocumentiTab from '../components/DocumentiTab';
import ProposteTab from '../components/ProposteTab';
import SchemaValutazioneList from './SchemaValutazioneList';
import VerbaleValutazioneList from './VerbaleValutazioneList';
import TrasparenzaPage from './TrasparenzaPage';
import { usePppApi } from '../services/pppApi';

// Nomina RUP sul procedimento (Multi-Tenant v3): il RUP va scelto tra quelli
// già associati e accettati per l'ente del procedimento. L'invito di un nuovo
// RUP all'ente avviene altrove (pagina "RUP dell'ente" dell'amministratore):
// qui si nomina soltanto tra gli associati.
function RupInline({ procedimento, refresh, _locate, canAssegna }) {
  const api = usePppApi();
  const { addToast } = useStateContext();
  const [editing, setEditing] = useState(false);
  const [rupUuid, setRupUuid] = useState('');
  const [associati, setAssociati] = useState([]);
  const [caricati, setCaricati] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing || caricati || procedimento.enteId == null) return;
    api.listUtentiRup(procedimento.enteId, {
      onSuccess: (data) => { setAssociati(Array.isArray(data) ? data : []); setCaricati(true); },
      onFormError: () => setCaricati(true),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const reset = () => { setEditing(false); setRupUuid(''); };

  const handleAssegna = () => {
    setSaving(true);
    api.assegnaRup(procedimento.id, rupUuid, {
      onSuccess: () => {
        setSaving(false);
        reset();
        refresh();
        addToast(<Toast duration={5} content={_locate('ppp_rup_assegnato') ?? 'RUP nominato sul procedimento.'} />);
      },
      onFormError: (err) => {
        setSaving(false);
        const msg = err?.errors?.rupUuid?.[0] ?? err?.message ?? 'Impossibile nominare il RUP.';
        addToast(<Toast duration={6} content={msg} />);
      },
      onError: () => setSaving(false),
    });
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <p className="font-medium">{procedimento.rupNome ?? '—'}</p>
        {canAssegna && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-blue-500 hover:underline"
          >
            {procedimento.rupNome ? _locate('ppp_modifica') : (_locate('ppp_rup_nomina') ?? 'Nomina RUP')}
          </button>
        )}
      </div>
    );
  }

  const options = associati.map((r) => ({ value: r.id, label: `${r.name} — ${r.email}` }));

  return (
    <div className="mt-1 space-y-2">
      {caricati && options.length === 0 ? (
        <p className="text-sm text-amber-600">
          {_locate('ppp_rup_nessun_associato')
            ?? 'Nessun RUP associato a questo ente. Invita prima un RUP dalla sezione "RUP dell\'ente".'}
        </p>
      ) : (
        <Select
          label={_locate('ppp_rup_seleziona') ?? 'Seleziona un RUP associato'}
          value={rupUuid}
          onChange={(e) => setRupUuid(e?.target ? e.target.value : e)}
          options={[{ value: '', label: '—' }, ...options]}
        />
      )}
      <div className="flex gap-2">
        <LoadingButton
          isLoading={saving}
          customFunc={rupUuid ? handleAssegna : undefined}
          text={_locate('ppp_rup_nomina') ?? 'Nomina RUP'}
        />
        <SimpleButton customFunc={reset} text={_locate('ppp_form_annulla')} />
      </div>
    </div>
  );
}

export default function ProcedimentoDetail() {
  const { id: rawId } = useParams();
  const id = rawId?.split(/[?&]/)[0];
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = location.state?.tab ?? 0;
  const { _locate, addToast } = useStateContext();
  const { procedimento, loading, error, refresh } = useProcedimento(id);
  const { scadenze, scadenzeUrgenti, refresh: refreshScadenze } = useScadenze(id);
  const { can, user } = useAuthUser();
  usePppAiContext('ppp.procedimento', { id: id ?? '' });

  useEffect(() => {
    if (!error) return;
    addToast(<Toast type="error" duration={5} content={_locate('ppp_procedimento_non_autorizzato') ?? 'Procedimento non trovato o accesso non consentito.'} />);
    navigate(user?.role === 'rup' ? '/ppp/assegnati' : '/ppp', { replace: true });
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || error) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!procedimento) {
    return null;
  }

  const scadenzeLabel = scadenzeUrgenti.length > 0
    ? `${_locate('ppp_tab_scadenze')} (${scadenzeUrgenti.length})`
    : _locate('ppp_tab_scadenze');

  const bloccato = !procedimento.rupNominato;
  const rupAssegnato = !!procedimento.rupId;

  const BloccoTab = () => (
    <div className="p-6 flex flex-col items-center justify-center text-center gap-3 min-h-[120px]">
      <p className="text-amber-700 font-medium text-sm">
        {rupAssegnato
          ? (_locate('ppp_blocco_atto_mancante') ?? 'Procedimento bloccato: genera il decreto di nomina RUP dalla tab "Atti" e approvalo per procedere.')
          : (_locate('ppp_blocco_rup_mancante') ?? 'Procedimento bloccato: assegna prima un RUP tramite la select in cima alla pagina.')}
      </p>
    </div>
  );

  const tabs = [
    {
      tabname: _locate('ppp_tab_fasi'),
      content: bloccato ? <BloccoTab /> : (
        <TimelineFasi
          fasi={procedimento.fasi}
          faseAttivaNumero={procedimento.faseAttivaNumero}
          procedimentoId={id}
          canAvanza={can('ppp.fasi.avanza')}
          onRefresh={(_faseId) => refresh(true)}
          _locate={_locate}
        />
      ),
    },
    {
      tabname: scadenzeLabel,
      content: bloccato ? <BloccoTab /> : (
        <ScadenzeWidget
          scadenze={scadenze}
          procedimentoId={id}
          onUpdate={refreshScadenze}
          canGestisci={can('ppp.scadenze.manage')}
          _locate={_locate}
        />
      ),
    },
    can('ppp.atti.view') && {
      tabname: _locate('ppp_tab_atti'),
      content: <AttiTab procedimento={procedimento} _locate={_locate} onProcedimentoChange={refresh} />,
    },
    {
      tabname: _locate('ppp_tab_proposte'),
      content: bloccato ? <BloccoTab /> : <ProposteTab procedimentoId={parseInt(id)} />,
    },
    can('ppp.documenti.view') && {
      tabname: _locate('ppp_tab_documenti'),
      content: bloccato ? <BloccoTab /> : <DocumentiTab procedimento={procedimento} _locate={_locate} />,
    },
    can('ppp.valutazione_comparativa.view') && {
      tabname: _locate('ppp_tab_schema_valutazione'),
      content: bloccato ? <BloccoTab /> : <SchemaValutazioneList procedimentoId={parseInt(id)} />,
    },
    can('ppp.verbale_valutazione.view') && {
      tabname: _locate('ppp_tab_verbale_valutazione'),
      content: bloccato ? <BloccoTab /> : <VerbaleValutazioneList procedimentoId={parseInt(id)} />,
    },
    can('ppp.trasparenza.view') && {
      tabname: _locate('ppp_tab_trasparenza'),
      content: bloccato ? <BloccoTab /> : <TrasparenzaPage procedimentoId={parseInt(id)} />,
    },
  ].filter(Boolean);

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/ppp')}
            className="text-sm text-blue-600 hover:underline mb-2 flex items-center gap-1"
          >
            ← {_locate('ppp_torna_lista')}
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{procedimento.titolo}</h1>
              <p className="text-gray-500 text-sm mt-1">{procedimento.enteConcedente}</p>
            </div>
            <span className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
              {procedimento.stato}
            </span>
          </div>

          {bloccato && (
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <span className="mt-0.5 text-amber-500 text-base leading-none">⚠</span>
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  {_locate('ppp_blocco_rup_titolo') ?? 'Procedimento in attesa di nomina RUP'}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {rupAssegnato
                    ? (_locate('ppp_blocco_atto_descrizione') ?? 'RUP assegnato. Ora genera il decreto di nomina dalla tab "Atti" e approvalo per sbloccare il procedimento.')
                    : (_locate('ppp_blocco_rup_descrizione') ?? 'Per procedere: assegna un RUP tramite la select, genera il decreto di nomina dalla tab "Atti" e approvalo.')}
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-400">{_locate('ppp_rup')}</span>
              <RupInline procedimento={procedimento} refresh={refresh} _locate={_locate} canAssegna={can('ppp.rup.assign')} />
            </div>
            <div>
              <span className="text-gray-400">{_locate('ppp_anno_programmazione')}</span>
              <p className="font-medium">{procedimento.annoProgrammazione ?? '—'}</p>
            </div>
            <div>
              <span className="text-gray-400">{_locate('ppp_fase_attiva')}</span>
              <p className="font-medium">
                {procedimento.faseAttivaNumero
                  ? `${_locate('ppp_tab_fasi')} ${procedimento.faseAttivaNumero}`
                  : _locate('ppp_non_avviato')}
              </p>
            </div>
            <div>
              <span className="text-gray-400">{_locate('ppp_importo_stimato')}</span>
              <p className="font-medium">
                {procedimento.importoStimato
                  ? `€ ${Number(procedimento.importoStimato).toLocaleString('it-IT')}`
                  : '—'}
              </p>
            </div>
          </div>
        </div>

        <Tabs tablist={tabs} activetab={initialTab} />
      </div>
    </div>
  );
}
